import { getDeliveryMediaBlob } from '@/api/deliveries';
import { getPublicShopBranding } from '@/api/shop';


export const deliveryTimestamp = (order) => {
  const confirmation = order.delivery_confirmation || {};
  return confirmation.time_delivered || confirmation.time_confirmed || confirmation.confirmed_at || '';
};

export const ordersForRun = (run, orders) => {
  const orderIds = new Set((run.stops || []).map((stop) => stop.order_id));
  return orders.filter((order) => orderIds.has(order.id));
};

export const resolveRunDriverName = (run, runOrders, drivers) => {
  if (run.driver_id) {
    return drivers.find(([id]) => id === run.driver_id)?.[1] || '—';
  }
  return runOrders.find((order) => order.delivery_confirmation?.confirmed_by_user_name)
    ?.delivery_confirmation.confirmed_by_user_name || '—';
};

export const ordersForDriver = (orders, driverName) => orders.filter((order) => (
  order.delivery_confirmation?.confirmed_by_user_name === driverName
  || order.assigned_driver_name === driverName
));

export const buildDriverRunSections = (driverOrders, runs) => {
  const runByOrderId = new Map();
  runs.forEach((run) => (run.stops || []).forEach((stop) => runByOrderId.set(stop.order_id, run)));

  const groups = new Map();
  driverOrders.forEach((order) => {
    const run = runByOrderId.get(order.id);
    const key = run?.id || '__no_run__';
    if (!groups.has(key)) groups.set(key, { run, orders: [] });
    groups.get(key).orders.push(order);
  });

  const earliestTimestamp = ({ orders }) => (
    orders.map(deliveryTimestamp).filter(Boolean).sort()[0] || ''
  );
  return Array.from(groups.values())
    .sort((left, right) => {
      const leftTime = earliestTimestamp(left);
      const rightTime = earliestTimestamp(right);
      if (leftTime && rightTime) return leftTime.localeCompare(rightTime);
      if (leftTime) return -1;
      if (rightTime) return 1;
      return 0;
    })
    .map(({ run, orders }) => ({ name: run?.name || 'No run', orders }));
};

// API records are JSON data. Copy at admission so an in-place change by a
// caller cannot change a document halfway through asynchronous preparation.
export const captureManifestSnapshot = (kind, input) => {
  const snapshot = JSON.parse(JSON.stringify(input));
  const { orders, runs, drivers, selectedDate, runFilter, driverFilter } = snapshot;
  let selectedOrders;
  let details;
  let selection;
  let missingRunOrders = 0;
  if (kind === 'run') {
    const run = runs.find((candidate) => candidate.id === runFilter);
    if (!run) throw new Error('Cannot print — selected run is unavailable');
    selectedOrders = ordersForRun(run, orders);
    const loadedIds = new Set(orders.map((order) => order.id));
    missingRunOrders = new Set((run.stops || []).map((stop) => stop.order_id)
      .filter((id) => id && !loadedIds.has(id))).size;
    details = { run, orders: selectedOrders, driverName: resolveRunDriverName(run, selectedOrders, drivers) };
    selection = `Run: ${run.name || run.id}`;
  } else {
    const driverName = drivers.find(([id]) => id === driverFilter)?.[1];
    if (!driverName) throw new Error('Cannot print — selected driver is unavailable');
    selectedOrders = ordersForDriver(orders, driverName);
    if (!selectedOrders.length) throw new Error('No orders found for this driver');
    details = { driverName, runSections: buildDriverRunSections(selectedOrders, runs) };
    selection = `Driver: ${driverName}`;
  }
  return {
    orders: selectedOrders,
    payload: {
      ...details,
      date: selectedDate,
      scope: {
        includedCount: selectedOrders.length, selection, missingRunOrders,
        limitReached: orders.length >= 200,
        nameSearch: snapshot.nameSearch?.trim() || '',
        addressSearch: snapshot.addressSearch?.trim() || '',
        sourceFilter: snapshot.sourceFilter || 'all',
      },
    },
  };
};

const aborted = () => new DOMException('Manifest preparation cancelled', 'AbortError');

// FileReader does not accept AbortSignal. Own its cancellation and impose a
// finite read deadline so a stalled reader cannot hold the print lease forever.
export const blobToDataUrl = (blob, signal) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  let settled = false;
  let timer;
  const finish = (error, value) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
    reader.onload = null;
    reader.onerror = null;
    reader.onabort = null;
    if (error) reject(error);
    else resolve(value);
  };
  const cancel = () => {
    finish(aborted());
    reader.abort();
  };
  if (signal?.aborted) { finish(aborted()); return; }
  signal?.addEventListener('abort', cancel, { once: true });
  reader.onload = () => finish(null, reader.result);
  reader.onerror = () => finish(reader.error || new Error('Signature read failed'));
  reader.onabort = () => finish(aborted());
  timer = setTimeout(() => {
    finish(new Error('Signature read timed out'));
    reader.abort();
  }, 10000);
  try { reader.readAsDataURL(blob); } catch (error) { finish(error); }
});

export const fetchManifestSignatureUrls = async (
  orders,
  token,
  loadMedia = getDeliveryMediaBlob,
  { signal, isCurrent = () => true } = {},
) => {
  const signaturePaths = [...new Set(orders
    .map((order) => (order.delivery_confirmation?.media_files || [])
      .find((media) => media.file_type === 'signature')?.storage_path)
    .filter(Boolean))];
  const signatureUrls = {};
  await Promise.all(signaturePaths.map(async (storagePath) => {
    if (signal?.aborted || !isCurrent()) return;
    try {
      const blob = await loadMedia(storagePath, { token, signal });
      if (signal?.aborted || !isCurrent()) return;
      const url = await blobToDataUrl(blob, signal);
      if (signal?.aborted || !isCurrent()) return;
      signatureUrls[storagePath] = url;
    } catch (error) {
      if (error?.name !== 'AbortError' && isCurrent()) {
        console.warn('Failed to prepare a manifest signature:', error);
      }
    }
  }));
  return signatureUrls;
};

export const fetchManifestShop = async (loadShop = getPublicShopBranding, options = {}) => {
  try {
    return await loadShop(options);
  } catch {
    return null;
  }
};
